const safeDecodePath = (rawValue = "") => {
  let current = String(rawValue || "");
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
};

const stripDiacritics = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const normalizePath = (path = "") => {
  if (!path) return "/";
  const [withoutQuery] = String(path).split(/[?#]/);
  const decoded = safeDecodePath(withoutQuery).trim();
  if (!decoded) return "/";
  const normalized = stripDiacritics(decoded)
    .toLowerCase()
    .replace(/\/{2,}/g, "/");
  const withLeadingSlash = normalized.startsWith("/") ? normalized : `/${normalized}`;
  return withLeadingSlash.length > 1 && withLeadingSlash.endsWith("/")
    ? withLeadingSlash.slice(0, -1)
    : withLeadingSlash;
};

const warnedPaths = new Set();
const capabilityEvents = new Set();

const parseBooleanFlag = (rawValue, defaultValue = false) => {
  if (rawValue === undefined || rawValue === null || rawValue === "") return defaultValue;
  const normalized = String(rawValue).trim().toLowerCase();
  return ["1", "true", "yes", "on"].includes(normalized);
};

export const isCapabilitiesStrictDefaultDeny = () =>
  parseBooleanFlag(process.env.REACT_APP_CAPABILITIES_STRICT_DEFAULT_DENY, false);

const shouldLogCapabilityDecisions = () => {
  if (process.env.NODE_ENV !== "production") return true;
  return parseBooleanFlag(process.env.REACT_APP_CAPABILITIES_LOG_DENIES, false);
};

const logCapabilityDecision = (level, eventKey, message) => {
  if (!shouldLogCapabilityDecisions()) return;
  if (capabilityEvents.has(eventKey)) return;
  capabilityEvents.add(eventKey);
  // eslint-disable-next-line no-console
  console[level](`[capabilities] ${message}`);
};

const devWarnUnmappedPath = (path, scope) => {
  if (process.env.NODE_ENV === "production") return;
  const key = `${scope}:${path}`;
  if (warnedPaths.has(key)) return;
  warnedPaths.add(key);
  logCapabilityDecision("warn", `legacy-allow:${scope}:${path}`, `Ruta sin regla explícita permitida temporalmente (${scope}): ${path}`);
};

const getActions = (capabilities) => capabilities?.data?.actions || {};

const getModules = (capabilities) => {
  const modules = capabilities?.data?.modules;
  return Array.isArray(modules) ? modules : [];
};

const getPermissions = (capabilities) => {
  const permissions = capabilities?.data?.permissions;
  return Array.isArray(permissions) ? permissions : [];
};

const hasAny = (source, values) => values.some((value) => Boolean(source[value]));
const hasAll = (source, values) => values.every((value) => Boolean(source[value]));
const listHasAny = (source, values) => values.some((value) => source.includes(value));
const listHasAll = (source, values) => values.every((value) => source.includes(value));

const evaluateRule = (rule, { capabilities, isSuperuser = false } = {}, meta = {}) => {
  if (!rule) return true;
  if (isSuperuser) return true;

  const {
    actionsAny = [],
    actionsAll = [],
    modulesAny = [],
    modulesAll = [],
    permissionsAny = [],
    permissionsAll = [],
    permissionPrefixesAny = [],
    permissionPrefixesAll = [],
    superuserOnly = false,
    fallback = false,
    allowWhileLoading = true,
  } = rule;

  if (superuserOnly) return Boolean(isSuperuser);
  const isLoading = capabilities?.isLoading !== false;
  const hasCapabilityPayload = Boolean(capabilities?.data);
  if (!hasCapabilityPayload) {
    return isLoading ? allowWhileLoading : fallback;
  }

  const actions = getActions(capabilities);
  const modules = getModules(capabilities);
  const permissions = getPermissions(capabilities);

  const denyWithLog = (reason) => {
    if (meta.kind && meta.target) {
      logCapabilityDecision(
        "warn",
        `deny:${meta.kind}:${meta.target}:${reason}`,
        `${meta.kind} denegado (${meta.target}) por falta de capability (${reason})`
      );
    }
    return false;
  };

  if (actionsAny.length && !hasAny(actions, actionsAny)) return denyWithLog("actionsAny");
  if (actionsAll.length && !hasAll(actions, actionsAll)) return denyWithLog("actionsAll");
  if (modulesAny.length && !listHasAny(modules, modulesAny)) return denyWithLog("modulesAny");
  if (modulesAll.length && !listHasAll(modules, modulesAll)) return denyWithLog("modulesAll");
  if (permissionsAny.length && !listHasAny(permissions, permissionsAny)) return denyWithLog("permissionsAny");
  if (permissionsAll.length && !listHasAll(permissions, permissionsAll)) return denyWithLog("permissionsAll");
  if (
    permissionPrefixesAny.length &&
    !permissionPrefixesAny.some((prefix) => permissions.some((perm) => String(perm).startsWith(prefix)))
  ) {
    return denyWithLog("permissionPrefixesAny");
  }
  if (
    permissionPrefixesAll.length &&
    !permissionPrefixesAll.every((prefix) => permissions.some((perm) => String(perm).startsWith(prefix)))
  ) {
    return denyWithLog("permissionPrefixesAll");
  }

  return true;
};

export const CAPABILITY_ROUTE_RULES = [
  // Entradas globales (evita warnings en flujos normales)
  { match: "exact", path: "/", rule: {} },
  { match: "exact", path: "/dashboard", rule: {} },
  { match: "exact", path: "/administracion", rule: {} },
  { match: "prefix", path: "/panel/autopilot", rule: {} },
  // Asistente: lo habilita el backend por administración (asistente_chat.piloto).
  { match: "prefix", path: "/panel/asistente", rule: {} },
  { match: "prefix", path: "/panel/super", rule: { superuserOnly: true, fallback: false } },
  { match: "exact", path: "/panel/active-users-uf", rule: { superuserOnly: true, fallback: false } },
  { match: "prefix", path: "/panel/ventas/inbox", rule: { superuserOnly: true, fallback: false } },

  // Servicios (granular)
  { match: "prefix", path: "/panel/reclamos", rule: { actionsAny: ["can_access_services", "can_services_view"] } },
  { match: "prefix", path: "/panel/servicios/reclamos-config", rule: { actionsAny: ["can_access_services", "can_services_view"] } },
  { match: "prefix", path: "/panel/servicios/reclamos-metricas", rule: { actionsAny: ["can_access_services", "can_services_view"] } },
  { match: "prefix", path: "/panel/servicios/encuestas/nueva", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/encuestas/agregar", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/presupuestos/nuevo", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/inventario/nuevo", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/amenitis/agregar", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/asambleas/agregar", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/encuestas/editar", rule: { actionsAny: ["can_services_edit"] } },
  { match: "prefix", path: "/panel/servicios/presupuestos/:id/editar", rule: { actionsAny: ["can_services_edit"] } },
  { match: "prefix", path: "/panel/servicios/gestion-alquileres/contratos/nuevo", rule: { actionsAny: ["can_services_create"] } },
  { match: "prefix", path: "/panel/servicios/inventario/:id/editar", rule: { actionsAny: ["can_services_edit"] } },
  { match: "prefix", path: "/panel/servicios/gestion-alquileres/contratos/:id/editar", rule: { actionsAny: ["can_services_edit"] } },
  // Mirar la cuenta no es editarla: alcanza con permiso de lectura.
  { match: "prefix", path: "/panel/servicios/gestion-alquileres/contratos/:id/cuenta-corriente", rule: { actionsAny: ["can_services_view", "can_access_services"] } },
  // Confirmar o rechazar un pago mueve plata: pide permiso de edición, no de lectura.
  { match: "prefix", path: "/panel/servicios/gestion-alquileres/pagos-informados", rule: { actionsAny: ["can_services_edit"] } },
  { match: "prefix", path: "/panel/servicios/gestion-alquileres/ajustes", rule: { actionsAny: ["can_services_edit"] } },
  // La tabla de índices es compartida entre administraciones: un valor mal
  // cargado afecta los ajustes de todas.
  { match: "prefix", path: "/panel/servicios/gestion-alquileres/indices", rule: { actionsAny: ["can_services_edit"] } },
  { match: "prefix", path: "/panel/servicios/amenitis/editar", rule: { actionsAny: ["can_services_edit"] } },
  { match: "prefix", path: "/panel/servicios/asambleas/editar", rule: { actionsAny: ["can_services_edit"] } },
  {
    match: "prefix",
    path: "/panel/servicios",
    rule: {
      actionsAny: [
        "can_access_services",
        "can_services_view",
        "can_access_insurance",
        "can_access_legal",
        "can_access_payment_plans",
        "can_access_tasks",
        "can_access_requests",
      ],
    },
  },

  // Sueldos (granular)
  { match: "prefix", path: "/panel/sueldos/listado/convenios/agregar", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/listado/alta-empleados/agregar", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/listado/conceptos-remunerativos/agregar", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/listado/conceptos-no-remunerativos/agregar", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/listado/descuento-convenio/agregar", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/listado/liquidacion-sueldos/agregar", rule: { actionsAny: ["can_generate_salary"] } },
  { match: "prefix", path: "/panel/sueldos/listado/liquidación-sueldos/agregar", rule: { actionsAny: ["can_generate_salary"] } },
  { match: "prefix", path: "/panel/sueldos/generar-liquidacion", rule: { actionsAny: ["can_generate_salary"] } },
  { match: "prefix", path: "/panel/sueldos/novedades/agregar", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/categorias-cct/nuevo", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/escalas-salariales/nuevo", rule: { actionsAny: ["can_salary_create"] } },
  { match: "prefix", path: "/panel/sueldos/listado/convenios/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/listado/alta-empleados/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/listado/conceptos-remunerativos/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/listado/conceptos-no-remunerativos/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/listado/descuento-convenio/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/novedades/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/categorias-cct/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/escalas-salariales/editar", rule: { actionsAny: ["can_salary_edit"] } },
  { match: "prefix", path: "/panel/sueldos/listado/alicuotas-patronales", rule: { actionsAny: ["can_access_salary", "can_salary_view"] } },
  { match: "prefix", path: "/panel/sueldos", rule: { actionsAny: ["can_access_salary", "can_salary_view"] } },

  // Correo (granular)
  { match: "prefix", path: "/panel/correo/compose", rule: { actionsAny: ["can_mail_create"] } },
  { match: "prefix", path: "/panel/correo/configuración", rule: { actionsAny: ["can_mail_special"] } },
  { match: "prefix", path: "/panel/correo/mail-config", rule: { actionsAny: ["can_mail_edit"] } },
  { match: "prefix", path: "/panel/correo/ai-settings", rule: { actionsAny: ["can_mail_special"] } },
  { match: "prefix", path: "/panel/correo/ai/settings", rule: { actionsAny: ["can_mail_special"] } },
  {
    match: "prefix",
    path: "/panel/comunicacion",
    rule: {
      actionsAny: [
        "can_access_mail",
        "can_mail_view",
        "can_access_whatsapp_templates",
        "can_access_services",
        "can_services_view",
      ],
    },
  },
  { match: "prefix", path: "/panel/correo", rule: { actionsAny: ["can_access_mail", "can_mail_view"] } },
  { match: "prefix", path: "/panel/chat", rule: { actionsAny: ["can_access_whatsapp_templates"], fallback: false } },

  // Liquidación (granular)
  { match: "prefix", path: "/panel/liquidacion/agregar", rule: { actionsAny: ["can_generate_liquidation"] } },
  { match: "prefix", path: "/panel/liquidación/agregar", rule: { actionsAny: ["can_generate_liquidation"] } },
  { match: "prefix", path: "/panel/liquidacion/grupo/agregar", rule: { actionsAny: ["can_liquidation_create"] } },
  { match: "prefix", path: "/panel/liquidacion/grupo-porcentual/agregar", rule: { actionsAny: ["can_liquidation_create"] } },
  { match: "prefix", path: "/panel/liquidacion/porcentaje-unidad/agregar", rule: { actionsAny: ["can_liquidation_create"] } },
  { match: "prefix", path: "/panel/liquidacion/concepto-expensa/agregar", rule: { actionsAny: ["can_liquidation_create"] } },
  { match: "prefix", path: "/panel/liquidacion/grupo/editar", rule: { actionsAny: ["can_liquidation_edit"] } },
  { match: "prefix", path: "/panel/liquidacion/grupo-porcentual/editar", rule: { actionsAny: ["can_liquidation_edit"] } },
  { match: "prefix", path: "/panel/liquidacion/porcentaje-unidad/editar", rule: { actionsAny: ["can_liquidation_edit"] } },
  { match: "prefix", path: "/panel/liquidacion/concepto-expensa/editar", rule: { actionsAny: ["can_liquidation_edit"] } },
  {
    match: "prefix",
    path: "/panel/liquidacion/apertura-conceptos",
    rule: { actionsAny: ["can_manage_liquidations", "can_manage_periods", "can_liquidation_special"] },
  },
  {
    match: "prefix",
    path: "/panel/liquidacion/ajuste-conceptos",
    rule: { actionsAny: ["can_manage_liquidations", "can_manage_periods", "can_liquidation_special"] },
  },
  { match: "prefix", path: "/panel/liquidacion/configuracion-pme", rule: { actionsAny: ["can_manage_payment_settings", "can_liquidation_special"] } },
  { match: "prefix", path: "/panel/liquidacion/configuracion-siro", rule: { actionsAny: ["can_manage_payment_settings", "can_liquidation_special"] } },
  { match: "prefix", path: "/panel/liquidación/configuración-pme", rule: { actionsAny: ["can_manage_payment_settings", "can_liquidation_special"] } },
  { match: "prefix", path: "/panel/liquidación/configuración-siro", rule: { actionsAny: ["can_manage_payment_settings", "can_liquidation_special"] } },
  { match: "exact", path: "/panel/liquidacion/cuentacorrientes_propietarios", rule: { actionsAny: ["can_current_accounts_view"], fallback: false } },
  { match: "prefix", path: "/panel/liquidacion", rule: { actionsAny: ["can_access_liquidation", "can_liquidation_view"] } },
  { match: "prefix", path: "/panel/liquidación", rule: { actionsAny: ["can_access_liquidation", "can_liquidation_view"] } },

  // Convenios de pago (cobranzas)
  {
    match: "prefix",
    path: "/panel/movimientos/cobranzas/convenios-pago/agregar",
    rule: { actionsAny: ["can_manage_convenios_pago"] },
  },
  {
    match: "prefix",
    path: "/panel/movimientos/cobranzas/convenios-pago/editar",
    rule: { actionsAny: ["can_manage_convenios_pago"] },
  },
  {
    match: "prefix",
    path: "/panel/movimientos/cobranzas/convenios-pago/renegociar",
    rule: { actionsAny: ["can_manage_convenios_pago"] },
  },
  {
    match: "prefix",
    path: "/panel/movimientos/cobranzas/convenios-pago",
    rule: { actionsAny: ["can_access_convenios_pago"] },
  },

  // Cuentas corrientes (granular base)
  { match: "prefix", path: "/panel/ctas_ctes/expensas-por-concepto", rule: { actionsAny: ["can_current_accounts_special"] } },
  { match: "prefix", path: "/panel/ctas_ctes", rule: { actionsAny: ["can_access_current_accounts", "can_current_accounts_view"] } },

  // Roles / Users / Configuración
  { match: "prefix", path: "/panel/configuración/listado/roles", rule: { actionsAny: ["can_manage_roles"], fallback: false } },
  { match: "prefix", path: "/panel/configuracion/listado/roles", rule: { actionsAny: ["can_manage_roles"], fallback: false } },
  { match: "exact", path: "/panel/manual-de-procedimiento", rule: { actionsAny: ["can_access_configuration"] } },
  // Novedades de WAM: para todos los usuarios del panel (el backend decide qué ve cada uno).
  { match: "exact", path: "/panel/novedades-wam", rule: {} },
  { match: "prefix", path: "/panel/configuracion/manual/videos", rule: { actionsAny: ["can_access_configuration"] } },
  { match: "prefix", path: "/panel/configuracion", rule: { actionsAny: ["can_access_configuration"] } },
  { match: "exact", path: "/panel/afip-wizard", rule: { actionsAny: ["can_access_configuration", "can_manage_payment_settings"] } },
  { match: "prefix", path: "/panel/configuración/listado/usuarios", rule: { actionsAny: ["can_manage_users"] } },
  { match: "prefix", path: "/panel/configuración/agregar/usuario", rule: { actionsAny: ["can_manage_users"] } },
  { match: "prefix", path: "/panel/configuración/editar/usuario", rule: { actionsAny: ["can_manage_users"] } },
  { match: "exact", path: "/panel/configuración", rule: { actionsAny: ["can_access_configuration"] } },
  { match: "prefix", path: "/panel/configuracion/listado/administradores", rule: { actionsAny: ["can_manage_admins"] } },
  { match: "prefix", path: "/panel/configuración/agregar/administrador", rule: { actionsAny: ["can_manage_admins"] } },
  { match: "prefix", path: "/panel/configuración/editar/administrador", rule: { actionsAny: ["can_manage_admins"] } },
  { match: "prefix", path: "/panel/configuración/listado/periodos", rule: { actionsAny: ["can_manage_periods"] } },
  { match: "prefix", path: "/panel/configuración/agregar/periodo", rule: { actionsAny: ["can_manage_periods"] } },
  { match: "prefix", path: "/panel/configuración/editar/periodo", rule: { actionsAny: ["can_manage_periods"] } },
  { match: "prefix", path: "/panel/configuración/listado/consorcios", rule: { actionsAny: ["can_manage_consortiums"] } },
  { match: "prefix", path: "/panel/configuración/agregar/consorcio", rule: { actionsAny: ["can_manage_consortiums"] } },
  { match: "prefix", path: "/panel/configuración/editar/consorcio", rule: { actionsAny: ["can_manage_consortiums"] } },
  { match: "prefix", path: "/panel/configuración/listado/propietarios", rule: { actionsAny: ["can_manage_owners"] } },
  { match: "prefix", path: "/panel/configuración/agregar/propietario", rule: { actionsAny: ["can_manage_owners"] } },
  { match: "prefix", path: "/panel/configuración/editar/propietario", rule: { actionsAny: ["can_manage_owners"] } },
  { match: "prefix", path: "/panel/configuración/importar/propietarios", rule: { actionsAny: ["can_manage_owners"] } },
  { match: "prefix", path: "/panel/configuración/encabezados", rule: { actionsAny: ["can_manage_report_headers"] } },
  { match: "prefix", path: "/panel/configuración/encabezado", rule: { actionsAny: ["can_manage_report_headers"] } },
  { match: "prefix", path: "/panel/folders/local", rule: { actionsAny: ["can_manage_admins"] } },
  { match: "prefix", path: "/panel/propietarios/hub", rule: { actionsAny: ["can_manage_owners"] } },
  { match: "prefix", path: "/panel/resumen-consorcio", rule: { actionsAny: ["can_manage_owners"] } },
  { match: "prefix", path: "/panel/configuración/accesos-directos", rule: { actionsAny: ["can_manage_dashboard_shortcuts"] } },
  { match: "prefix", path: "/panel/configuracion/accesos-directos", rule: { actionsAny: ["can_manage_dashboard_shortcuts"] } },
  { match: "prefix", path: "/panel/configuracion/mercadopago", rule: { actionsAny: ["can_manage_payment_settings"] } },
  { match: "prefix", path: "/panel/configuración/mercadopago", rule: { actionsAny: ["can_manage_payment_settings"] } },
  { match: "prefix", path: "/panel/acceso_terceros", rule: { actionsAny: ["can_manage_third_party_access"] } },

  // Finanzas / Movimientos / Reportes sensibles
  { match: "prefix", path: "/panel/movimientos/gastos/agregar", rule: { permissionPrefixesAny: ["gastos.add_"] } },
  { match: "prefix", path: "/panel/movimientos/gastos/editar", rule: { permissionPrefixesAny: ["gastos.change_"] } },
  { match: "exact", path: "/panel/movimientos/pagos", rule: { permissionPrefixesAny: ["pagos.view_", "pagos.add_", "pagos.change_"], fallback: false } },
  { match: "prefix", path: "/panel/movimientos/listado/pagos", rule: { permissionPrefixesAny: ["pagos.view_", "pagos.add_", "pagos.change_"], fallback: false } },
  { match: "prefix", path: "/panel/movimientos/pagos/listado/cheques", rule: { permissionPrefixesAny: ["pagos.view_", "pagos.add_", "pagos.change_"], fallback: false } },
  { match: "prefix", path: "/panel/movimientos/pagos/alta-solpay", rule: { permissionPrefixesAny: ["pagos.add_", "pagos.change_"], fallback: false } },
  { match: "prefix", path: "/panel/movimientos/pagos/agregar", rule: { permissionPrefixesAny: ["pagos.add_"] } },
  { match: "prefix", path: "/panel/movimientos/pagos/editar", rule: { permissionPrefixesAny: ["pagos.change_"] } },
  { match: "prefix", path: "/panel/google/local", rule: { actionsAny: ["can_access_manual_pdf_upload"], fallback: false } },
  { match: "prefix", path: "/panel/movimientos/cobranzas/agregar", rule: { permissionPrefixesAny: ["cobranzas.add_"] } },
  { match: "prefix", path: "/panel/movimientos/cobranzas/editar", rule: { permissionPrefixesAny: ["cobranzas.change_"] } },
  { match: "prefix", path: "/panel/movimientos/cobranzas/borradores", rule: { permissionPrefixesAny: ["cobranzas.view_", "cobranzas.change_"] } },
  { match: "prefix", path: "/panel/movimientos/otros-ingresos/agregar", rule: { permissionPrefixesAny: ["cobranzas.add_"] } },
  { match: "prefix", path: "/panel/movimientos/otros-ingresos/editar", rule: { permissionPrefixesAny: ["cobranzas.change_"] } },
  { match: "prefix", path: "/panel/gastos/facturas-no-pagadas", rule: { permissionPrefixesAny: ["gastos.view_", "pagos.add_", "pagos.change_"], fallback: false } },
  { match: "exact", path: "/panel/finanzas/listado/bancos", rule: { actionsAny: ["can_banks_view"], fallback: false } },
  { match: "prefix", path: "/panel/finanzas/agregar/banco", rule: { actionsAny: ["can_banks_create"], fallback: false } },
  { match: "prefix", path: "/panel/finanzas/editar/banco", rule: { actionsAny: ["can_banks_edit"], fallback: false } },
  { match: "prefix", path: "/panel/finanzas/no-identificados", rule: { actionsAny: ["can_access_unidentified_movements"], fallback: false } },
  { match: "prefix", path: "/panel/finanzas", rule: { actionsAny: ["can_view_financial_modules"] } },
  { match: "prefix", path: "/panel/movimientos", rule: { actionsAny: ["can_view_financial_modules", "can_access_convenios_pago"] } },
  { match: "prefix", path: "/panel/solicitudes", rule: { actionsAny: ["can_access_requests"], fallback: false } },
  { match: "prefix", path: "/panel/servicios/solicitudes", rule: { actionsAny: ["can_access_requests"], fallback: false } },
  { match: "prefix", path: "/panel/servicios/pedidos", rule: { actionsAny: ["can_access_requests"], fallback: false } },
  { match: "prefix", path: "/panel/tareas", rule: { actionsAny: ["can_access_tasks"], fallback: false } },
  { match: "prefix", path: "/panel/seguros", rule: { actionsAny: ["can_access_insurance"], fallback: false } },
  { match: "prefix", path: "/panel/legales", rule: { actionsAny: ["can_access_legal"], fallback: false } },
  { match: "prefix", path: "/panel/planes-pago", rule: { actionsAny: ["can_access_payment_plans"], fallback: false } },
  { match: "prefix", path: "/panel/planes_pago", rule: { actionsAny: ["can_access_payment_plans"], fallback: false } },
  { match: "prefix", path: "/panel/auditoria", rule: { actionsAny: ["can_access_audit"], fallback: false } },
  { match: "prefix", path: "/reportes/propietarios", rule: { actionsAny: ["can_access_owner_debt_certificate"], fallback: false } },
  { match: "prefix", path: "/panel/reportes/financiero", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/conciliacion-fondo-operativo", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/gastos", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/pagos", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/facturas_no_pagadas", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/morosos", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/morosos-convenios", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/convenios-pago", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/deuda-propietarios-convenios", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/cobranzas-convenios", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes/informe-anual", rule: { actionsAny: ["can_view_sensitive_reports"] } },
  { match: "prefix", path: "/panel/reportes", rule: { actionsAny: ["can_view_general_reports"] } },
  { match: "prefix", path: "/contabilidad-admin", rule: { actionsAny: ["can_manage_admins", "can_view_financial_modules"] } },
];

// Rutas auditadas: si no tienen regla explícita => deny.
const AUDITED_ROUTE_PREFIXES = [
  "/panel/configuración",
  "/panel/configuracion",
  "/panel/finanzas",
  "/panel/movimientos",
  "/panel/reportes",
  "/panel/servicios",
  "/panel/sueldos",
  "/panel/comunicacion",
  "/panel/correo",
  "/panel/liquidacion",
  "/panel/liquidación",
  "/panel/ctas_ctes",
  "/panel/resumen-consorcio",
];

export const CAPABILITY_SIDEBAR_RULES = {
  nav_claims_area: { actionsAny: ["can_access_services", "can_services_view"] },
  nav_configuration: {
    actionsAny: [
      "can_access_configuration",
      "can_manage_users",
      "can_manage_roles",
      "can_assign_roles",
      "can_manage_admins",
      "can_manage_periods",
      "can_manage_consortiums",
      "can_manage_owners",
      "can_manage_report_headers",
      "can_manage_dashboard_shortcuts",
      "can_manage_payment_settings",
      "can_manage_third_party_access",
    ],
  },
  nav_finance: { actionsAny: ["can_view_financial_modules"] },
  nav_movements: { actionsAny: ["can_view_financial_modules"] },
  nav_reports: { actionsAny: ["can_view_general_reports", "can_view_sensitive_reports", "can_access_owner_debt_certificate"] },
  nav_services: {
    actionsAny: [
      "can_access_services",
      "can_services_view",
      "can_access_insurance",
      "can_access_legal",
      "can_access_payment_plans",
      "can_access_tasks",
      "can_access_requests",
    ],
  },
  nav_utilities: { actionsAny: ["can_access_requests", "can_access_tasks"] },
  nav_current_accounts: { actionsAny: ["can_access_current_accounts"] },
  nav_salary: { actionsAny: ["can_access_salary"] },
  nav_mail: { actionsAny: ["can_access_mail"] },
  nav_documents: { actionsAny: ["can_manage_admins"] },
  nav_audit: { actionsAny: ["can_access_audit"] },
  nav_whatsapp_templates: { actionsAny: ["can_access_whatsapp_templates"] },
  nav_expenses_area: { actionsAny: ["can_view_financial_modules", "can_access_insurance", "can_access_payment_plans"] },
  nav_collections_area: { actionsAny: ["can_view_financial_modules", "can_access_convenios_pago"] },
  nav_liquidations_area: {
    actionsAny: [
      "can_access_liquidation",
      "can_liquidation_view",
      "can_current_accounts_view",
      "can_manage_payment_settings",
      "can_liquidation_special",
    ],
  },
  nav_communication_area: {
    actionsAny: [
      "can_access_mail",
      "can_mail_view",
      "can_access_whatsapp_templates",
      "can_access_services",
      "can_services_view",
    ],
  },
};

export const CAPABILITY_UI_RULES = {
  users_manage: { actionsAny: ["can_manage_users"], fallback: false },
  roles_manage: { actionsAny: ["can_manage_roles"], fallback: false },
  roles_assign: { actionsAny: ["can_assign_roles"], fallback: false },
  financial_modules_view: { actionsAny: ["can_view_financial_modules"] },
  settings_view: { actionsAny: ["can_access_configuration"] },
  users_effective_permissions_view: { actionsAny: ["can_manage_roles", "can_assign_roles"], fallback: false },
  admins_manage: { actionsAny: ["can_manage_admins"], fallback: false },
  periods_manage: { actionsAny: ["can_manage_periods"], fallback: false },
  consortiums_manage: { actionsAny: ["can_manage_consortiums"], fallback: false },
  owners_manage: { actionsAny: ["can_manage_owners"], fallback: false },
  report_headers_manage: { actionsAny: ["can_manage_report_headers"], fallback: false },
  shortcuts_manage: { actionsAny: ["can_manage_dashboard_shortcuts"], fallback: false },
  payment_settings_manage: { actionsAny: ["can_manage_payment_settings"], fallback: false },
  third_party_access_manage: { actionsAny: ["can_manage_third_party_access"], fallback: false },
  reports_general_view: { actionsAny: ["can_view_general_reports"], fallback: false },
  reports_sensitive_view: { actionsAny: ["can_view_sensitive_reports"], fallback: false },
  services_access: { actionsAny: ["can_access_services"], fallback: false },
  services_area_access: { actionsAny: ["can_access_services", "can_services_view"], fallback: false },
  insurance_access: { actionsAny: ["can_access_insurance"], fallback: false },
  legal_access: { actionsAny: ["can_access_legal"], fallback: false },
  payment_plans_access: { actionsAny: ["can_access_payment_plans"], fallback: false },
  owner_debt_certificate_access: { actionsAny: ["can_access_owner_debt_certificate"], fallback: false },
  tasks_access: { actionsAny: ["can_access_tasks"], fallback: false },
  requests_access: { actionsAny: ["can_access_requests"], fallback: false },
  manual_pdf_upload_access: { actionsAny: ["can_access_manual_pdf_upload"], fallback: false },
  unidentified_movements_access: { actionsAny: ["can_access_unidentified_movements"], fallback: false },
  salary_access: { actionsAny: ["can_access_salary"], fallback: false },
  mail_access: { actionsAny: ["can_access_mail"], fallback: false },
  services_view: { actionsAny: ["can_services_view"], fallback: false },
  services_create: { actionsAny: ["can_services_create"], fallback: false },
  services_edit: { actionsAny: ["can_services_edit"], fallback: false },
  services_delete: { actionsAny: ["can_services_delete"], fallback: false },
  services_special: { actionsAny: ["can_services_special"], fallback: false },
  salary_view: { actionsAny: ["can_salary_view"], fallback: false },
  salary_create: { actionsAny: ["can_salary_create"], fallback: false },
  salary_generate: { actionsAny: ["can_generate_salary"], fallback: false },
  salary_edit: { actionsAny: ["can_salary_edit"], fallback: false },
  salary_delete: { actionsAny: ["can_salary_delete"], fallback: false },
  salary_special: { actionsAny: ["can_salary_special"], fallback: false },
  mail_view: { actionsAny: ["can_mail_view"], fallback: false },
  mail_create: { actionsAny: ["can_mail_create"], fallback: false },
  mail_edit: { actionsAny: ["can_mail_edit"], fallback: false },
  mail_delete: { actionsAny: ["can_mail_delete"], fallback: false },
  mail_special: { actionsAny: ["can_mail_special"], fallback: false },
  liquidation_access: { actionsAny: ["can_access_liquidation"], fallback: false },
  liquidation_view: { actionsAny: ["can_liquidation_view"], fallback: false },
  liquidation_create: { actionsAny: ["can_liquidation_create"], fallback: false },
  liquidation_generate: { actionsAny: ["can_generate_liquidation"], fallback: false },
  liquidation_edit: { actionsAny: ["can_liquidation_edit"], fallback: false },
  liquidation_delete: { actionsAny: ["can_liquidation_delete"], fallback: false },
  liquidation_special: { actionsAny: ["can_liquidation_special"], fallback: false },
  current_accounts_access: { actionsAny: ["can_access_current_accounts"], fallback: false },
  current_accounts_view: { actionsAny: ["can_current_accounts_view"], fallback: false },
  current_accounts_create: { actionsAny: ["can_current_accounts_create"], fallback: false },
  current_accounts_edit: { actionsAny: ["can_current_accounts_edit"], fallback: false },
  current_accounts_delete: { actionsAny: ["can_current_accounts_delete"], fallback: false },
  current_accounts_special: { actionsAny: ["can_current_accounts_special"], fallback: false },
  banks_view: { actionsAny: ["can_banks_view"], fallback: false },
  banks_create: { actionsAny: ["can_banks_create"], fallback: false },
  banks_edit: { actionsAny: ["can_banks_edit"], fallback: false },
  banks_delete: { actionsAny: ["can_banks_delete"], fallback: false },
  financial_create: { permissionPrefixesAny: ["gastos.add_", "pagos.add_", "cobranzas.add_"], fallback: false },
  financial_edit: { permissionPrefixesAny: ["gastos.change_", "pagos.change_", "cobranzas.change_"], fallback: false },
  convenios_pago_access: { actionsAny: ["can_access_convenios_pago"], fallback: false },
  convenios_pago_manage: { actionsAny: ["can_manage_convenios_pago"], fallback: false },
};

const getRouteRuleRank = (entry) => {
  const rulePath = normalizePath(entry.path);
  return {
    rulePath,
    matchPriority: entry.match === "exact" ? 2 : 1,
    pathLength: rulePath.length,
  };
};

const findRouteRule = (path) => {
  const normalized = normalizePath(path);
  const matches = CAPABILITY_ROUTE_RULES
    .map((entry, index) => ({ entry, index, ...getRouteRuleRank(entry) }))
    .filter(({ entry, rulePath }) => {
      if (entry.match === "exact") return normalized === rulePath;
      return normalized.startsWith(rulePath);
    })
    .sort((left, right) => {
      if (right.matchPriority !== left.matchPriority) {
        return right.matchPriority - left.matchPriority;
      }
      if (right.pathLength !== left.pathLength) {
        return right.pathLength - left.pathLength;
      }
      return left.index - right.index;
    });
  return matches[0]?.entry;
};

export const canAccessRoutePath = (path, context) => {
  const normalized = normalizePath(path);
  const entry = findRouteRule(normalized);
  if (!entry) {
    const strictDefaultDeny = isCapabilitiesStrictDefaultDeny();
    const isAudited = AUDITED_ROUTE_PREFIXES.some((prefix) =>
      normalized.startsWith(normalizePath(prefix))
    );
    if (strictDefaultDeny || isAudited) {
      logCapabilityDecision(
        "warn",
        `deny:route:${normalized}:missing-rule`,
        `Ruta denegada por falta de regla explícita (${normalized})`
      );
      return false;
    }
    devWarnUnmappedPath(normalized, "route");
    return true;
  }
  return evaluateRule(entry.rule, context, { kind: "route", target: normalized });
};

export const canAccessSidebarItem = (itemKey, context) => {
  const rule = CAPABILITY_SIDEBAR_RULES[itemKey];
  if (!rule) {
    if (isCapabilitiesStrictDefaultDeny()) {
      logCapabilityDecision(
        "warn",
        `deny:sidebar:${itemKey}:missing-rule`,
        `Sidebar item denegado por falta de regla explícita (${itemKey})`
      );
      return false;
    }
    devWarnUnmappedPath(itemKey, "sidebar");
    return true;
  }
  return evaluateRule(rule, context, { kind: "sidebar", target: itemKey });
};

export const canAccessUiAction = (actionKey, context) => {
  const rule = CAPABILITY_UI_RULES[actionKey];
  if (!rule) {
    if (isCapabilitiesStrictDefaultDeny()) {
      logCapabilityDecision(
        "warn",
        `deny:ui:${actionKey}:missing-rule`,
        `Acción UI denegada por falta de regla explícita (${actionKey})`
      );
      return false;
    }
    devWarnUnmappedPath(actionKey, "ui");
    return true;
  }
  return evaluateRule(rule, context, { kind: "ui", target: actionKey });
};

export const getRouteCapabilityRule = (path) => findRouteRule(path)?.rule || null;
